import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import {
  findBlockElementAtPoint,
  isInsideNonEditable,
  createOverlayElement,
  setupIntersectionObserver,
  setupScrollHandler,
  setupResizeObserver,
  cleanupInspector,
} from 'utils/block-inspector-common';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import { triggerChat } from 'components/organisms/chat/use-chat-trigger';
import { InspectorPopover } from './InspectorPopover';
import { AskAiToolbar } from '../ask-ai/ui/command-processor/ask-ai-toolbar';
import './styles.css';

const ElementInspectorName = 'elementInspector';
const ElementInspectorKey = new PluginKey(ElementInspectorName);

// Configuration for the extension
const nonEditableSections = [
  'ACCOUNT_DETAILS',
  'SIGNAL_DETAILS',
  'EMAIL_DETAILS',
  'MEETING_DETAILS',
  'feedback-trigger',
];

const extensionConfig = {
  showOverlay: true,
  overlayClassName: 'inspector-overlay',
  labelClassName: 'inspector-label',
  excludeSelectors: [],
  includeSelectors: [],
  nonEditableSections,
};

export const ElementInspectorExtension = Extension.create({
  name: ElementInspectorName,

  addOptions() {
    return {
      onElementClick: null,
      showOverlay: true,
      overlayClassName: 'inspector-overlay',
      labelClassName: 'inspector-label',
      pageType: null,
      sourceId: null,
      sources: null,
      isDefaultReadOnly: false,
    };
  },

  addProseMirrorPlugins() {
    let overlayElement: HTMLElement | null = null;
    let hoveredElement: HTMLElement | null = null;
    let popoverContainer: HTMLDivElement | null = null;
    let tooltipElement: HTMLElement | null = null;
    let isInspecting = false;
    let lastClickedElement: HTMLElement | null = null;
    let intersectionObserver: IntersectionObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let scrollHandler: (() => void) | null = null;

    return [
      new Plugin({
        key: ElementInspectorKey,
        state: {
          init: () => ({
            hoveredElement: null,
            overlayVisible: false,
            overlayPosition: { top: 0, left: 0, width: 0, height: 0 },
            overlayLabel: '',
          }),
          apply: (tr, oldState) => {
            const meta = tr.getMeta(ElementInspectorKey);

            return meta ? { ...oldState, ...meta } : oldState;
          },
        },
        view: (_view) => {
          // Create overlay element
          if (this.options.showOverlay) {
            overlayElement = createOverlayElement(
              this.options.overlayClassName,
            );
          }

          // Create tooltip element
          tooltipElement = document.createElement('div');
          tooltipElement.className = 'inspector-tooltip';
          tooltipElement.textContent = 'Select to ask AI';
          document.body.appendChild(tooltipElement);

          return {
            update: (_view, _prevState) => {
              const currentState = ElementInspectorKey.getState(_view.state);

              if (overlayElement) {
                if (currentState.overlayVisible) {
                  overlayElement.classList.remove('hidden');
                  overlayElement.classList.add('visible');
                  overlayElement.style.top = `${currentState.overlayPosition.top - 8}px`;
                  overlayElement.style.left = `${currentState.overlayPosition.left - 7}px`;
                  overlayElement.style.width = `${currentState.overlayPosition.width + 10}px`;
                  overlayElement.style.height = `${currentState.overlayPosition.height + 16}px`;
                } else {
                  if (!isInspecting) {
                    overlayElement.classList.remove('visible');
                    overlayElement.classList.add('hidden');
                  }
                }
              }
            },
            destroy: () => {
              if (overlayElement) {
                overlayElement.remove();
                overlayElement = null;
              }

              if (tooltipElement) {
                tooltipElement.remove();
                tooltipElement = null;
              }
            },
          };
        },
        props: {
          handleDOMEvents: {
            mousemove: (_view, event) => {
              if (isInspecting || !this.options.showOverlay) {
                return false;
              }

              const element = findBlockElementAtPoint(
                event.clientX,
                event.clientY,
                this.editor.options.element as HTMLElement,
                extensionConfig,
              );

              // Always update tooltip position to follow cursor when hovering over an element
              if (element && tooltipElement) {
                tooltipElement.classList.add('visible');
                // Position tooltip to the right and slightly below the cursor
                tooltipElement.style.left = `${event.clientX + 16}px`;
                tooltipElement.style.top = `${event.clientY + 8}px`;
              }

              if (element !== hoveredElement) {
                // Remove hoverable class from previous element
                if (hoveredElement) {
                  hoveredElement.classList.remove('inspector-hoverable');
                }

                hoveredElement = element;

                if (
                  element &&
                  isInsideNonEditable(
                    element,
                    '[data-rl-node]',
                    extensionConfig.nonEditableSections,
                  )
                ) {
                  if (overlayElement) {
                    overlayElement.classList.remove('visible');
                    overlayElement.classList.add('hidden');
                  }
                  document.body.style.cursor = '';

                  return false;
                }

                if (element) {
                  const rect = element.getBoundingClientRect();
                  const tr = _view.state.tr;

                  tr.setMeta(ElementInspectorKey, {
                    hoveredElement: element,
                    overlayVisible: true,
                    overlayPosition: {
                      top: rect.top,
                      left: rect.left,
                      width: rect.width,
                      height: rect.height,
                    },
                    overlayLabel: 'Inspector',
                  });

                  _view.dispatch(tr);
                } else {
                  // Hide tooltip when not hovering over element
                  if (tooltipElement) {
                    tooltipElement.classList.remove('visible');
                  }

                  if (!lastClickedElement) {
                    const tr = _view.state.tr;

                    tr.setMeta(ElementInspectorKey, {
                      hoveredElement: null,
                      overlayVisible: false,
                    });
                    _view.dispatch(tr);
                  }
                }
              }

              return false;
            },
            click: (_view, _event) => {
              if (!hoveredElement || isInspecting) {
                return false;
              }

              if (
                isInsideNonEditable(
                  hoveredElement,
                  '[data-node-view-wrapper]',
                  extensionConfig.nonEditableSections,
                )
              ) {
                return false;
              }

              _event.preventDefault();
              _event.stopPropagation();

              if (popoverContainer) {
                popoverContainer.remove();
                popoverContainer = null;
              }

              const rect = hoveredElement.getBoundingClientRect();
              const startPos = this.editor.view.posAtCoords({
                left: rect.left,
                top: rect.top,
              });
              const endPos = this.editor.view.posAtCoords({
                left: rect.right,
                top: rect.bottom,
              });

              if (startPos !== null && endPos !== null && endPos.pos) {
                lastClickedElement = hoveredElement;

                const tr = this.editor.state.tr.setSelection(
                  TextSelection.create(
                    this.editor.state.doc,
                    startPos.pos,
                    endPos.pos,
                  ),
                );

                this.editor.view.dispatch(tr);

                // Create popover
                popoverContainer = document.createElement('div');
                popoverContainer.className = 'ask-ai-inspector-popover';
                popoverContainer.style.top = `${window.scrollY + (_event.clientY || 0) + 8}px`;
                popoverContainer.style.left = `${window.scrollX + (_event.clientX || 0)}px`;
                document.body.appendChild(popoverContainer);

                isInspecting = true;

                const editorElement = this.editor.options
                  .element as HTMLElement;

                // Function to update overlay position
                const updateOverlayPosition = () => {
                  if (lastClickedElement && overlayElement) {
                    const rect = lastClickedElement.getBoundingClientRect();

                    overlayElement.style.top = `${rect.top - 8}px`;
                    overlayElement.style.left = `${rect.left - 7}px`;
                    overlayElement.style.width = `${rect.width + 10}px`;
                    overlayElement.style.height = `${rect.height + 16}px`;
                  }
                };

                // Set up scroll handler for continuous position updates
                const scrollContainer = document.querySelector(
                  '[class*="document__ContentWrapper"]',
                ) as HTMLElement;

                if (lastClickedElement && overlayElement) {
                  // Set up IntersectionObserver for visibility control
                  intersectionObserver = setupIntersectionObserver(
                    lastClickedElement,
                    overlayElement,
                    scrollContainer,
                    updateOverlayPosition,
                  );

                  // Set up scroll handler for continuous position updates
                  scrollHandler = setupScrollHandler(
                    lastClickedElement,
                    overlayElement,
                    scrollContainer,
                    editorElement,
                    updateOverlayPosition,
                  );

                  // Set up ResizeObserver for layout changes
                  resizeObserver = setupResizeObserver(
                    lastClickedElement,
                    overlayElement,
                    editorElement,
                    scrollContainer,
                    updateOverlayPosition,
                  );
                }

                const root = portalWithAppContext({
                  container: popoverContainer,
                  children: this.editor.isEditable ? (
                    <AskAiToolbar
                      editor={this.editor}
                      config={{ showEnhanceWithMyNotes: true }}
                    />
                  ) : (
                    <InspectorPopover
                      element={lastClickedElement}
                      onClose={() => {
                        root.unmount();
                        if (popoverContainer) {
                          popoverContainer.remove();
                          popoverContainer = null;
                        }
                      }}
                      onOpenChat={(message: string) => {
                        // Extract Tiptap JSON context of the clicked element
                        let elementContext = '';

                        if (lastClickedElement) {
                          try {
                            // Find the Tiptap node view wrapper
                            const nodeViewWrapper = lastClickedElement.closest(
                              '[data-node-view-wrapper]',
                            );

                            if (nodeViewWrapper) {
                              // Get the position of the element in the document
                              const startPos = this.editor.view.posAtCoords({
                                left: lastClickedElement.getBoundingClientRect()
                                  .left,
                                top: lastClickedElement.getBoundingClientRect()
                                  .top,
                              });
                              const endPos = this.editor.view.posAtCoords({
                                left: lastClickedElement.getBoundingClientRect()
                                  .right,
                                top: lastClickedElement.getBoundingClientRect()
                                  .bottom,
                              });

                              if (startPos !== null && endPos !== null) {
                                // Extract the complete block content from start to end position
                                const fragment = this.editor.state.doc.slice(
                                  startPos.pos,
                                  endPos.pos,
                                );

                                if (fragment) {
                                  // Get text content instead of JSON
                                  elementContext = fragment.content.textBetween(
                                    0,
                                    fragment.content.size,
                                  );
                                }
                              }
                            }
                          } catch {
                            // Error extracting element context
                            elementContext =
                              'Unable to extract Tiptap element context';
                          }
                        }

                        // Use the new triggerChat function instead of custom events
                        triggerChat({
                          message,
                          elementContext,
                          sourceType: this.options.pageType,
                          sourceId: this.options.sourceId,
                          sources: this.options.sources ?? undefined,
                        });
                      }}
                    />
                  ),
                });

                const handleClickOutside = (event: MouseEvent) => {
                  const isEditable = this.options.isDefaultReadOnly
                    ? true
                    : this.editor.isEditable;

                  if (
                    isEditable &&
                    event.target &&
                    !(event.target as HTMLElement).closest(
                      '.enhance-with-notes-input-popover',
                    ) &&
                    !(event.target as HTMLElement).closest(
                      '.editor-popover-content',
                    ) &&
                    popoverContainer &&
                    !popoverContainer.contains(event.target as Node)
                  ) {
                    root.unmount();
                    popoverContainer.remove();
                    popoverContainer = null;

                    isInspecting = false;

                    // Clean up observers and listeners
                    cleanupInspector(
                      intersectionObserver,
                      null, // mutationObserver
                      resizeObserver,
                      scrollHandler,
                      scrollContainer,
                      editorElement,
                      null, // rafId
                    );

                    intersectionObserver = null;
                    resizeObserver = null;
                    scrollHandler = null;

                    // Keep overlay on last clicked element
                    if (lastClickedElement) {
                      const rect = lastClickedElement.getBoundingClientRect();
                      const tr = this.editor.view.state.tr;

                      tr.setMeta(ElementInspectorKey, {
                        hoveredElement: lastClickedElement,
                        overlayVisible: true,
                        overlayPosition: {
                          top: rect.top,
                          left: rect.left,
                          width: rect.width,
                          height: rect.height,
                        },
                        overlayLabel: 'Inspector',
                      });
                      this.editor.view.dispatch(tr);
                    }

                    document.removeEventListener('click', handleClickOutside);
                  }
                };

                // Listen for closeInspector event from chat close
                const handleCloseInspector = () => {
                  isInspecting = false;

                  // Clean up observers and listeners
                  cleanupInspector(
                    intersectionObserver,
                    null, // mutationObserver
                    resizeObserver,
                    scrollHandler,
                    scrollContainer,
                    editorElement,
                    null, // rafId
                  );

                  intersectionObserver = null;
                  resizeObserver = null;
                  scrollHandler = null;

                  // Remove overlay from last clicked element and continue inspector mode
                  if (lastClickedElement) {
                    lastClickedElement = null;
                    const tr = this.editor.view.state.tr;

                    tr.setMeta(ElementInspectorKey, {
                      hoveredElement: null,
                      overlayVisible: false,
                    });
                    this.editor.view.dispatch(tr);
                  }

                  document.removeEventListener('click', handleClickOutside);
                  window.removeEventListener(
                    'closeInspector',
                    handleCloseInspector,
                  );
                };

                window.addEventListener('closeInspector', handleCloseInspector);

                setTimeout(() => {
                  document.addEventListener('click', handleClickOutside);
                }, 0);
              }

              const tr = _view.state.tr;

              tr.setMeta(ElementInspectorKey, { overlayVisible: false });
              _view.dispatch(tr);

              return true;
            },
            mouseleave: (_view, _event) => {
              if (isInspecting) {
                return false;
              }

              const editorElement = this.editor.options.element as HTMLElement;
              const relatedTarget = _event.relatedTarget as Node | null;

              if (!relatedTarget || !editorElement.contains(relatedTarget)) {
                if (hoveredElement) {
                  hoveredElement = null;
                  const tr = _view.state.tr;

                  tr.setMeta(ElementInspectorKey, {
                    hoveredElement: null,
                    overlayVisible: false,
                  });
                  _view.dispatch(tr);
                }
              }

              return false;
            },
          },
        },
      }),
    ];
  },
});
